import { useEffect, useMemo } from "react";
import { useLoader, useThree } from "@react-three/fiber";
import { GLTFLoader } from "three/examples/jsm/loaders/GLTFLoader.js";
import { MeshoptDecoder } from "three/examples/jsm/libs/meshopt_decoder.module.js";
import * as THREE from "three";
import { markLoadDone, reportLoadProgress } from "./loadProgress";

/**
 * Loads a purchased/authored glTF room. Real geometry, real parallax in every
 * direction (unlike a single-view splat), used for the walkable-office PoC.
 */
export function GltfRoom({
  url,
  scale = 1,
  position = [0, 0, 0],
  rotationDeg = [0, 0, 0],
  shadows = false,
}: {
  url: string;
  scale?: number;
  position?: [number, number, number];
  rotationDeg?: [number, number, number];
  /**
   * Cast and receive real-time shadows. Only for models with no baked lighting
   * (see EnvironmentSpec.outdoor); a baked room would double-darken.
   */
  shadows?: boolean;
}) {
  const gltf = useLoader(
    GLTFLoader,
    url,
    (loader) => {
      (loader as GLTFLoader).setMeshoptDecoder(MeshoptDecoder);
    },
    (e) => reportLoadProgress(url, e.loaded, e.lengthComputable ? e.total : 0),
  );

  const scene = useMemo(() => {
    const cloned = gltf.scene.clone(true);
    cloned.traverse((obj) => {
      obj.visible = true;
      const mesh = obj as THREE.Mesh;
      if ((mesh as THREE.Mesh).isMesh) {
        mesh.castShadow = shadows;
        mesh.receiveShadow = shadows;
        // Indoor rooms keep frustumCulled off (camera sits inside geometry).
        // Outdoors we want culling — drawing every lounger behind the camera
        // was a large part of the 2 GB Chrome tab.
        mesh.frustumCulled = shadows;
        const mat = mesh.material as THREE.MeshStandardMaterial | THREE.MeshStandardMaterial[];
        const mats = Array.isArray(mat) ? mat : [mat];
        for (const m of mats) {
          if (!m) continue;
          // DoubleSide doubles fill-rate and breaks shadow maps. Only keep it
          // for indoor baked rooms where some authored normals face inward.
          m.side = shadows ? THREE.FrontSide : THREE.DoubleSide;
          if ("envMapIntensity" in m) {
            // A baked room only wants a hint of environment on top of the light
            // already painted into its textures; a raw-PBR model outdoors gets
            // sky IBL, but not so much that it lifts the sun's shadows away.
            (m as THREE.MeshStandardMaterial).envMapIntensity = shadows ? 0.55 : 0.5;
          }
        }
      }
    });
    cloned.updateMatrixWorld(true);
    return cloned;
  }, [gltf, shadows]);

  // Signal the app that geometry + textures are decoded and committed, so the
  // branded loader can hand over the controls. gl.compile() forces shader
  // compilation now (not on first frame) so "ready" means truly renderable.
  const { gl, scene: rootScene, camera } = useThree();
  useEffect(() => {
    let raf = 0;
    try {
      gl.compile(rootScene, camera);
    } catch {
      /* compile is best-effort */
    }
    // One frame later everything is uploaded to the GPU.
    raf = requestAnimationFrame(() => {
      (window as unknown as {
        __cfGltfReady?: boolean;
        __cfGltfReadyUrl?: string;
        __cfScene?: THREE.Object3D;
        __cfCamera?: THREE.Camera;
        __cfTHREE?: typeof THREE;
      }).__cfGltfReady = true;
      (window as unknown as { __cfGltfReadyUrl?: string }).__cfGltfReadyUrl = url;
      // Debug hooks for headless desk-placement raycasts (harmless in prod).
      (window as unknown as { __cfScene?: THREE.Object3D }).__cfScene = rootScene;
      (window as unknown as { __cfCamera?: THREE.Camera }).__cfCamera = camera;
      (window as unknown as { __cfTHREE?: typeof THREE }).__cfTHREE = THREE;
      markLoadDone(url);
      window.dispatchEvent(new CustomEvent("cf-gltf-ready", { detail: { url } }));
    });
    return () => cancelAnimationFrame(raf);
  }, [scene, gl, rootScene, camera, url]);

  const d2r = (d: number) => (d * Math.PI) / 180;

  return (
    <primitive
      object={scene}
      scale={scale}
      position={position}
      rotation={[d2r(rotationDeg[0]), d2r(rotationDeg[1]), d2r(rotationDeg[2])]}
    />
  );
}